// 全域 axios 實例 + CSRF 攔截 + Loading 控制
// 元件內呼叫: import { myAjax } from '@/Common/api' 或 window.myAjax

import axios from 'axios'
import { ui } from './ui.js'
import { networkState, markServerReachable, markServerUnreachable } from './network.js'
import { getOfflineLoginMarker } from './offlineAuth.js'

// === 全域 Loading 控制 (_Layout.cshtml 放 <div id="globalLoader" hidden>...</div>) ===
let activeRequests = 0

function showGlobalLoader() {
  activeRequests++
  const el = document.getElementById('globalLoader')
  if (el) el.hidden = false
}

function hideGlobalLoader() {
  activeRequests = Math.max(0, activeRequests - 1)
  if (activeRequests === 0) {
    const el = document.getElementById('globalLoader')
    if (el) el.hidden = true
  }
}

// ════════════════════════════════════════════════════════════════════════
// 離線處理器（2026-10-01，離線編輯工項／單價分析）
// 某些「查參考資料」的 API（查工項主檔、搜尋工項、編碼規則、章碼計數…）離線時可以改用本機
// 下載好的資料回答——登記在這裡，呼叫端（BudgetItemEdit.vue、更換工項、挑選工項、規則精靈…）
// 完全不用改：離線時（或送出後才發現連不到伺服器）這幾支網址直接由本機處理器回應，形狀跟線上
// 一樣。處理器回 undefined＝這台裝置沒有可用的本機資料，照原本的離線行為（提示需要連線）。
// 處理器本身在 Common/offlineReference.js 登記。
// ════════════════════════════════════════════════════════════════════════
const offlineHandlers = new Map()

/** 登記某支 API 的離線處理器：handler(body) → 回應的 data（同線上 JSON），或 undefined＝沒有本機資料。 */
export function registerOfflineHandler(url, handler) {
  offlineHandlers.set(String(url).toLowerCase(), handler)
}

function offlineHandlerFor(url) {
  const path = String(url || '').split('?')[0].toLowerCase()
  return offlineHandlers.get(path) || null
}

function parseBody(data) {
  if (data === undefined || data === null || data === '') return {}
  if (typeof data === 'string') { try { return JSON.parse(data) } catch { return {} } }
  return data
}

/** 用本機處理器回答；沒有本機資料回 null。 */
async function answerLocally(config) {
  const handler = offlineHandlerFor(config.url)
  if (!handler) return null
  const data = await handler(parseBody(config.data))
  if (data === undefined) return null
  return { data, status: 200, statusText: 'OK (offline)', headers: {}, config: { ...config, __offlineLocal: true }, request: {} }
}

const isOfflineNow = () => !networkState.online || !!getOfflineLoginMarker()

// === 建立 axios 實例 ===
// baseURL 讀 window.__APP_BASE__ (由 _VueScriptsPartial 用 Url.Content("~/") 注入)。
// - 部署在 /MyApp 子應用 → "/MyApp/"
// - 部署在根目錄        → "/"
// 前端所有 ajax 不寫死 host root,UsePathBase 才會生效。
// ⚠ 60 秒是「預設值」不是天花板——單次呼叫要更長逾時（例如大批量匯入）
//   直接在呼叫端多帶第三個參數：`myAjax.post(url, data, { timeout: 600_000 })`。
//   axios 核心會把這個 per-call config 合併進實例預設值（逐欄覆寫，不是整份
//   取代），不需要另外改這裡——這是 axios 本來就有的行為，不是這個檔案自己
//   實作的機制，見 tests/unit/apiTimeout.test.mjs 的驗證。
export const myAjax = axios.create({
  baseURL: (typeof window !== 'undefined' && window.__APP_BASE__) || '/',
  timeout: 60_000,
  withCredentials: true,
})

// === Request: 自動帶 anti-forgery token + 顯示 loading ===
myAjax.interceptors.request.use(
  (config) => {
    // config.suppressLoading: true → 這次請求不開全螢幕遮罩（例如 autosave，
    // 每 30 秒閃一次遮罩使用者受不了）。呼叫端：myAjax.post(url, body, { suppressLoading: true })。
    // ⚠ 這個旗標本來就在，只是一直沒有人真的用過——用的時候務必連同下面
    // response/error 攔截器裡「對稱扣計數」那段一起看，兩邊要用同一個判斷式。
    if (!config.suppressLoading) showGlobalLoader()

    // 從 Razor View 的隱藏欄位讀取 anti-forgery token
    const tokenInput = document.querySelector('input[name="__RequestVerificationToken"]')
    if (tokenInput) {
      config.headers['RequestVerificationToken'] = tokenInput.value
    }
    // 離線且這支有本機處理器：不送出網路請求，改由本機回答（見上面「離線處理器」）。
    if (isOfflineNow() && offlineHandlerFor(config.url)) {
      const original = config.adapter
      config.adapter = async (cfg) => (await answerLocally(cfg)) ?? (original ? axios.getAdapter(original)(cfg) : axios.getAdapter(axios.defaults.adapter)(cfg))
    }
    return config
  },
  (error) => {
    hideGlobalLoader()
    return Promise.reject(error)
  },
)

// PWA 第三階段（殼側，2026-09-18 下）：只在「確定拿到一個成功的登入後回應」
// 之後才通知 SW 去預快取 /Budget/Index、/Budget/Add、/Budget/Edit 這三支殼——
// 比在 Layout 掛一段判斷式更準：能走到這裡代表這次請求不是 -1919（未登入），
// SW 收到訊息當下用同一份 cookie 重打那三支，兩邊看到的登入狀態天生一致，
// 不需要另外傳遞「現在是不是登入」這個狀態。只發一次，不是每個成功回應都發——
// 這三支殼很少變、沒必要每個 API 成功就重打一次。
let shellPrecacheRequested = false
function requestShellPrecache() {
  if (shellPrecacheRequested) return
  if (typeof navigator === 'undefined' || !navigator.serviceWorker?.controller) return
  shellPrecacheRequested = true
  navigator.serviceWorker.controller.postMessage({ type: 'pcces-precache-shell' })
}

// === Response: 統一處理未登入重導 / 隱藏 loading ===
myAjax.interceptors.response.use(
  (response) => {
    // ⚠ 一定要跟請求端的判斷式對稱：suppressLoading 的請求從沒讓 showGlobalLoader()
    //   加過計數，這裡如果無條件 hideGlobalLoader() 會把「別的、沒有 suppress 的
    //   請求」加的計數也扣掉——如果剛好有兩個請求同時飛，suppress 的那個先回來，
    //   遮罩會在真正的請求還沒做完時就被提早關掉。
    if (!response.config?.suppressLoading) hideGlobalLoader()
    // 本機處理器回答的：沒有真的連到伺服器，不能拿來判斷站台可達
    if (response.config?.__offlineLocal) return response

    // 2026-09-30 站台可達性偵測：能走到這個攔截器就代表 HTTP 層真的收到回應
    // （包含 -1919 這種業務層「請重新登入」），比等 30 秒週期探測更即時——
    // 見 Common/network.js 的 markServerReachable()。
    markServerReachable()

    // 後端 SessionFilter 用 result === -1919 表示「請重新登入」並回傳 url
    if (response.data && response.data.result === -1919 && response.data.url) {
      // ⚠⚠⚠ PWA 第四階段（離線登入）：這個瀏覽器分頁如果正處於「離線登入」
      // 狀態（sessionStorage 有標記，見 Common/offlineAuth.js），代表使用者
      // 一開始就沒有真正的伺服器 Session（本機比對通過而已）——任何 API 呼叫
      // 一律會被 [SessionFilter] 判定未登入回這個 -1919，如果照舊導去登入頁，
      // 使用者會被反覆踢出正在看的本機資料畫面。改成提示「離線中，僅能使用
      // 本機資料」、不導頁，讓他繼續留在目前畫面——真正「要不要重新登入」交給
      // 下面的 `window.addEventListener('online', ...)`，偵測到連線恢復才主動
      // 提示並導去登入頁。
      if (getOfflineLoginMarker()) {
        ui.toast('離線中，僅能使用本機資料', 'warn')
        return null
      }
      // 防禦:若後端漏帶 PathBase (回 "/Login/Index" 而部署在子應用),自動補 __APP_BASE__
      let url = response.data.url
      const base = (typeof window !== 'undefined' && window.__APP_BASE__) || '/'
      if (base !== '/' && url.startsWith('/') && !url.toLowerCase().startsWith(base.toLowerCase())) {
        url = base.replace(/\/$/, '') + url
      }
      window.location.href = url
      return null
    }
    requestShellPrecache()
    return response
  },
  async (error) => {
    if (!error.config?.suppressLoading) hideGlobalLoader()
    const cfg = error.config || {}
    // 送出後才發現連不到伺服器（沒有 HTTP 回應）：有本機處理器的查詢改用本機資料回答，不報錯。
    if (!error.response && !cfg.__offlineLocal && offlineHandlerFor(cfg.url)) {
      markServerUnreachable()
      const local = await answerLocally(cfg).catch(() => null)
      if (local) return local
    }
    const fullUrl = (cfg.baseURL || '') + (cfg.url || '')
    console.error('[myAjax error]', fullUrl, error.message)
    // 2026-09-30 站台可達性偵測：`error.response` 存在代表 HTTP 層真的有回應
    // （只是業務層判定失敗，例如 4xx/5xx），站台本身是可達的；沒有 `response`
    // 才是真正的網路層問題（斷線／逾時／CORS），立即標記不可達並馬上重測一次
    // ——見 Common/network.js 的 markServerUnreachable()。這一步要放在下面
    // `networkState.online` 判斷之前：不可達要先反映到 networkState，下面的
    // 判斷才會拿到最新的值。
    if (error.response) markServerReachable()
    else markServerUnreachable()
    // ⚠ PWA 第二階段（離線查閱）：這個功能需要連線但目前離線，統一在這裡提示
    // 一次，不用每個呼叫端各自判斷——`networkState.online` 是全站唯一的連線
    // 狀態來源（見 Common/network.js），跟 autosave 佇列徽章讀同一個值。
    // 只在確定離線時提示；連線正常但伺服器端出錯的情況維持原樣交給呼叫端
    // 自己的 catch 顯示訊息，不在這裡重複跳提示。
    if (!networkState.online) {
      // ⚠⚠ 第三階段（殼側）新增：`config.offlineSilent: true` 的呼叫（例如
      // `/Budget/GetList`／`GetEdit` 這類離線時必然失敗、但呼叫端自己會接手
      // 顯示「本機模式」畫面的呼叫）不跳這個全站 toast、也不讓呼叫端走
      // catch——直接 resolve 一個好辨識的 `null`，呼叫端配合
      // `networkState.online` 判斷「是離線造成的、不是其他錯誤」。用法：
      // `const resp = await myAjax.post(url, body, { offlineSilent: true })`，
      // `resp` 離線時是 `null`。**不要**對一般呼叫預設套用這個行為——沒有
      // 標記的呼叫維持原樣（跳 toast＋reject），這是呼叫端明確加入才有的行為。
      if (cfg.offlineSilent) {
        return Promise.resolve(null)
      }
      ui.toast('目前離線，這個功能需要連線才能使用', 'warn')
    }
    return Promise.reject(error)
  },
)

// 回線先讓編輯器完成 IndexedDB 交易，再導向真正的登入。
// 離線身分標記保留至線上登入成功，避免背景同步使用未建立的 Session。
export async function requestOnlineLogin() {
    if (!getOfflineLoginMarker()) return
    const pending = []
    window.dispatchEvent(new CustomEvent('pcces-before-online-login', {
      detail: { waitUntil: (promise) => pending.push(promise) },
    }))
    try { await Promise.all(pending) } catch {
      ui.toast('本機變更尚未儲存，請先重試儲存再登入', 'error')
      return
    }
    const base = (window.__APP_BASE__ || '/').replace(/\/+$/, '')
    const seq = new URLSearchParams(window.location.search).get('seq')
    sessionStorage.setItem('pcces-offline-resume', /\/Budget\/Edit$/i.test(window.location.pathname) && /^\d+$/.test(seq || '')
      ? base + '/Budget/Edit?seq=' + seq : base + '/Budget/Index')
    ui.toast('網路已恢復，本機變更已保留，請重新登入以同步', 'info')
    window.location.href = base + '/Login/Index?offlineShell=1'
}
// 2026-09-30：改監聽 network.js 的 'pcces-online-reachable'（`networkState.online`
// 從 false 變 true 那一刻才會發），不是原生 'online'——Wi-Fi 連著但站台連不到時，
// 原生 'online' 根本不會再發一次；而 Wi-Fi 剛好也是斷線又恢復的一般情境，
// 'pcces-online-reachable' 一樣會發（network.js 的 'online' 事件處理器裡會呼叫
// checkServerReachable()，可達的話就會走到 recompute() 裡發這個事件），
// 不用另外疊加原生 'online' 監聽。
if (typeof window !== 'undefined') window.addEventListener('pcces-online-reachable', requestOnlineLogin)

// 暴露成全域,讓 Razor View 內的 inline script 也能用
if (typeof window !== 'undefined') {
  window.myAjax = myAjax
}

/**
 * ⚠⚠ 刻意例外：不是 myAjax——axios 不支援 fetch 的 `keepalive` 選項
 * （XHR-based adapter 沒有對應機制），離場（pagehide）時要「盡力送出去、
 * 不等回應」就只能繞開 axios 直接用 fetch。目前唯一呼叫端是
 * BudgetEdit.vue 的即改即存離場補送（見該檔 handlePageHide()）。
 *
 * ⚠ 不走 myAjax 意味著這裡**手動重做**兩件 myAjax 攔截器本來就有做的事：
 *   1. anti-forgery header —— 讀同一個 Razor View 埋的 hidden input，
 *      跟 myAjax 的請求攔截器同一個 selector，不要另外發明。
 *   2. PathBase —— 跟 myAjax 的 baseURL 同一個來源（window.__APP_BASE__），
 *      同一套「去頭去尾補一個斜線」的組法。
 *   兩邊以後如果要改（例如 token 的欄位名稱），這裡要記得一起改。
 * ⚠ 不顯示全域 loading（離場時顯示遮罩沒有意義，頁面都要卸載了）、
 *   不處理 -1919 重新登入導頁（離場時導頁也沒有意義）、不重試、
 *   fetch 本身失敗就放棄——這是「盡力而為」不是保證送達，pagehide
 *   期間瀏覽器本來就不保證任何非同步工作能跑完。
 */
export function postKeepalive(url, body) {
  if (typeof window === 'undefined' || typeof fetch !== 'function') return
  const base = window.__APP_BASE__ || '/'
  const fullUrl = base.replace(/\/+$/, '') + '/' + String(url).replace(/^\/+/, '')
  const headers = { 'Content-Type': 'application/json' }
  const tokenInput = document.querySelector('input[name="__RequestVerificationToken"]')
  if (tokenInput) headers['RequestVerificationToken'] = tokenInput.value
  try {
    fetch(fullUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      keepalive: true,
      credentials: 'same-origin',
    })
  } catch { /* 盡力而為，離場期間失敗就放棄，不影響卸載流程 */ }
}

export default myAjax
